import { Payments as SalaryIcon } from '@mui/icons-material';
import { Box, CircularProgress, Paper, Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderBanner from '../components/PageHeaderBanner';
import DashboardTab from './salary/DashboardTab';
import EmployeesTab from './salary/EmployeesTab';
import HolidaysTab from './salary/HolidaysTab';
import LeavesTab from './salary/LeavesTab';
import PayrollTab from './salary/PayrollTab';
import PenaltiesTab from './salary/PenaltiesTab';
import RolesTab from './salary/RolesTab';
import ScheduleTab from './salary/ScheduleTab';
import ShiftsTab from './salary/ShiftsTab';
import { useSalaryCoreData } from './salary/useSalaryCoreData';

/**
 * Top-level Salary / Payroll page.
 *
 * The sub-tabs (Dashboard, Employees, Roles, Shifts, Schedule, Leaves,
 * Holidays, Payroll) are implemented as independent components under
 * `pages/salary/` and communicate with the backend through the reusable
 * `useRequest` hook. The domain types in `domain/salary/` are the only
 * coupling between the tabs — the whole module can be extracted and
 * reused in a standalone React + Express stack.
 */
const Salary = () => {
    const { t } = useTranslation();
    const [tab, setTab] = useState(0);
    const { employees, roles, shifts, holidays, loading, reload } = useSalaryCoreData();

    return (
        <Box sx={{ p: 2 }}>
            <PageHeaderBanner
                icon={<SalaryIcon />}
                title={t('salary.title')}
                subtitle={t('salary.subtitle')}
                accentColor="#ca8a04"
            />
            <Paper sx={{ mb: 2 }}>
                <Tabs
                    value={tab}
                    onChange={(_event, value) => setTab(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab label={t('salary.tabs.dashboard')} />
                    <Tab label={t('salary.tabs.employees')} />
                    <Tab label={t('salary.tabs.roles')} />
                    <Tab label={t('salary.tabs.shifts')} />
                    <Tab label={t('salary.tabs.schedule')} />
                    <Tab label={t('salary.tabs.leaves')} />
                    <Tab label={t('salary.tabs.holidays')} />
                    <Tab label={t('salary.tabs.payroll')} />
                    <Tab label={t('salary.tabs.penalties')} />
                </Tabs>
            </Paper>
            {loading ? (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box>
                    {tab === 0 && <DashboardTab />}
                    {tab === 1 && (
                        <EmployeesTab employees={employees} roles={roles} reload={reload} />
                    )}
                    {tab === 2 && <RolesTab roles={roles} reload={reload} />}
                    {tab === 3 && <ShiftsTab shifts={shifts} roles={roles} reload={reload} />}
                    {tab === 4 && (
                        <ScheduleTab employees={employees} shifts={shifts} holidays={holidays} />
                    )}
                    {tab === 5 && <LeavesTab employees={employees} />}
                    {tab === 6 && <HolidaysTab holidays={holidays} reload={reload} />}
                    {tab === 7 && <PayrollTab employees={employees} />}
                    {tab === 8 && <PenaltiesTab />}
                </Box>
            )}
        </Box>
    );
};

export default Salary;
